/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { SubmitHandler } from "react-hook-form";
import { When } from "react-if";
import { FlexContainer, Spacer, theme } from "@nordcloud/gnui";
import { ValidationError } from "~/generated/graphql";
import { SelectableBox } from "~/components/SelectableBox";
import { AwsFormAuto } from "~/views/accounts/AddCapacity/CapacityForm/Forms/AwsFormAuto";
import { AwsFormManual } from "~/views/accounts/AddCapacity/CapacityForm/Forms/AwsFormManual";
import { FormData } from "~/views/cloudAccounts/BillingData/aws/validators";
import { TabWidget } from "../components/Tab";

const enum ActiveTab {
  AUTO = "auto",
  MANUAL = "manual",
}

type Props = {
  onSubmit: SubmitHandler<FormData>;
  onPrevStep: () => void;
  validationErrors?: ValidationError[];
};

export function AwsForm({
  onSubmit,
  onPrevStep,
  validationErrors = [],
}: Props) {
  const [activeTab, setActiveTab] = React.useState<ActiveTab>(ActiveTab.AUTO);

  return (
    <>
      <FlexContainer
        gap={theme.spacing.spacing04}
        wrap="nowrap"
        css={{ maxWidth: "60rem", margin: "0 auto" }}
      >
        <SelectableBox
          selected={activeTab === ActiveTab.AUTO}
          onClick={() => setActiveTab(ActiveTab.AUTO)}
        >
          <TabWidget
            subtitle="Create Cost and Usage Report"
            title="Automatic (Recommended)"
            description="CloudFormation stack will be deployed in the US-EAST-1 region."
          />
        </SelectableBox>
        <SelectableBox
          selected={activeTab === ActiveTab.MANUAL}
          onClick={() => setActiveTab(ActiveTab.MANUAL)}
        >
          <TabWidget
            subtitle="Create Cost and Usage Report"
            title="Manual"
            description="CloudFormation stack will be deployed in a chosen region."
          />
        </SelectableBox>
      </FlexContainer>

      <Spacer height={theme.spacing.spacing07} />
      <FlexContainer wrap="wrap" direction="column" alignItems="stretch">
        <When condition={activeTab === ActiveTab.AUTO}>
          <AwsFormAuto
            validationErrors={validationErrors}
            onSubmit={onSubmit}
            onPrevStep={onPrevStep}
          />
        </When>

        <When condition={activeTab === ActiveTab.MANUAL}>
          <AwsFormManual
            validationErrors={validationErrors}
            onSubmit={onSubmit}
            onPrevStep={onPrevStep}
          />
        </When>
      </FlexContainer>
    </>
  );
}
